import React, { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';

import { Flex, FlexColumn } from 'src/components/flex';
import convertFileSize from 'src/utils/convert-file-size';

interface Props {
    files: File[];
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

export default function FileAttachments({
    files,
    setFiles,
}: Props) {
    const handleRemoveFile = (index: number) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const fileSizeUnit = (size: number) => {
        if (size < 1024) {
            return 'bytes';
        }
        if (size > 1024 * 1024) {
            return 'MB';
        }
        return 'KB';
    };

    const formatName = (name: string) => {
        const length = 15;

        if (name.length <= length) {
            return name;
        }
        return `${name.substring(0, length / 2)}...${name.substring(name.length - 1 - length / 2, name.length)}`;
    };

    const tempUrls = useMemo(() => {
        return files.map((file, index) => ({
            url: URL.createObjectURL(file),
            name: formatName(file.name),
            size: file.size,
            type: file.type,
            index
        }));
    }, [files]);

    return (
        <Flex mobileFlexDirection="column" flexWrap="wrap">
            {tempUrls.map((file) => (
                <Flex
                    justifyContent="space-between"
                    mt={1}
                    p={1}
                    maxWidth="300px"
                    key={file.url}
                    flexWrap="wrap"
                    sx={{
                        backgroundColor: '#F7F7F7',
                        borderRadius: 4
                    }}
                >
                    <Flex>
                        <Flex alignItems="center">
                            <Avatar sx={{ bgcolor: '#10B981' }}>
                                {file.type.startsWith('image/') ?
                                    (
                                        <Avatar
                                            src={file.url}
                                            alt={file.name}
                                            sx={{ width: '100%', height: '100%' }}
                                        />
                                    ) :
                                    <InsertDriveFileIcon sx={{ fontSize: 20, color: '#FFFFFF' }} />
                                }
                            </Avatar>
                        </Flex>
                        <Flex alignItems="center" maxWidth="250px">
                            <FlexColumn>
                                <Typography
                                    variant="body2"
                                    width="120px"
                                    fontSize="12px"
                                    sx={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <strong>{file.name}</strong>
                                </Typography>
                                <Typography variant="caption">
                                    {`${convertFileSize(file.size)} ${fileSizeUnit(file.size)}`}
                                </Typography>
                            </FlexColumn>
                            <IconButton onClick={() => handleRemoveFile(file.index)}>
                                <CloseIcon sx={{ color: '#ADADAD' }} />
                            </IconButton>
                        </Flex>
                    </Flex>
                </Flex>
            ))}
        </Flex>
    );
}